import React, { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { MdFitnessCenter, MdRepeat } from "react-icons/md";

import { ExerciseData } from "../../interfaces";
import { getBaseChartOptions } from "./Utils/chart-config";
import {
  formatChart,
  formatFeedbackPoints,
  getDaySplitAnnotations,
  getProgressionSummary,
} from "./Utils/chart-utils";

import { useCustomContextMenu } from "src/components/UseContextMenu";
import { useOverlay } from "src/screens/checkout/OverlayContext";
import "./styles.css";

type Props = {
  data: ExerciseData[];
  exerciseName: string;
};

function useAppDarkMode() {
  const [isDark, setIsDark] = useState(
    document.querySelector(".App")?.classList.contains("dark") ?? false,
  );

  React.useEffect(() => {
    const app = document.querySelector(".App");
    if (!app) return;

    const observer = new MutationObserver(() => {
      setIsDark(app.classList.contains("dark"));
    });

    observer.observe(app, { attributes: true, attributeFilter: ["class"] });

    // Atualiza imediatamente caso o tema mude antes do observer
    setIsDark(app.classList.contains("dark"));

    return () => observer.disconnect();
  }, []);

  return isDark;
}

const ProgressionChart: React.FC<Props> = ({ data, exerciseName }) => {
  const overlay = useOverlay();
  const isDark = useAppDarkMode();

  const summary = getProgressionSummary(data);
  const chartData = formatChart(data);
  const annotations = getDaySplitAnnotations(chartData.categories);
  const feedback = formatFeedbackPoints(data, ShowFeedback);

  function exportSummaryAsXML() {
    const days = chartData.categories;
    const reps = chartData.reps;
    const weights = chartData.weight;
    const dates = chartData.dates;

    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<Progression>\n`;

    days.forEach((day, idx) => {
      xml += `  <Day>\n`;
      xml += `    <Label>${day}</Label>\n`;
      xml += `    <Date>${dates[idx]}</Date>\n`;
      xml += `    <Weight>${weights[idx]}</Weight>\n`;
      xml += `    <Reps>${reps[idx]}</Reps>\n`;
      xml += `  </Day>\n`;
    });

    xml += `</Progression>`;

    const blob = new Blob([xml], { type: "application/xml" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${exerciseName.replace(/\s+/g, "_")}_summary.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function ShowFeedback(e: string) {
    overlay.setShow(
      <div className="flex h-full flex-1" onPointerDown={overlay.dismiss}>
        <div
          className={"m-auto flex-col items-center rounded bg-white p-3 shadow"}
        >
          <div>
            <h3 className="font-bold">Feedback do Aluno</h3>
          </div>
          <p>{e}</p>
        </div>
      </div>,
    );
  }

  // Memoize options para evitar recriação desnecessária
  const options = useMemo(
    () =>
      getBaseChartOptions({
        categories: chartData.categories,
        reps: chartData.reps,
        weight: chartData.weight,
        dates: chartData.dates,
        daySplit: annotations,
        feedbackPoints: feedback,
        isDark: isDark,
      }),
    [chartData, annotations, feedback, isDark],
  );

  const [series] = useState<ApexAxisChartSeries>([
    {
      name: "PESO",
      type: "column",
      data: chartData.weight,
    },
    {
      name: "REPS",
      type: "line",
      data: chartData.reps,
    },
  ]);

  const menu = useCustomContextMenu([
    {
      title: "Exportar resumo como XML",
      icon: () => <MdFitnessCenter size={16} />,
      action: exportSummaryAsXML,
    },
  ]);

  return (
    <div className="flex flex-col gap-2" onContextMenu={menu.showMenu}>
      <menu.ContextMenu />
      <div className="flex flex-row items-center justify-between">
        <h3 className="text-base font-semibold dark:text-white">
          {exerciseName}
        </h3>

        <div className="flex flex-row gap-2 text-xs uppercase text-zinc-700 dark:text-zinc-300">
          <span className="flex items-center gap-1 rounded bg-zinc-200 px-2 py-1 dark:bg-zinc-700">
            <MdFitnessCenter size={16} />
            <span>{summary.avgWeightChangePct}</span>
          </span>
          <span className="flex items-center gap-1 rounded bg-zinc-200 px-2 py-1 dark:bg-zinc-700">
            <MdRepeat size={16} />
            <span>{summary.avgRepsChangePct}</span>
          </span>
        </div>
      </div>

      <ReactApexChart
        options={options}
        series={series}
        type="line"
        height="235px"
        width="100%"
      />
    </div>
  );
};

export default ProgressionChart;
